import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

interface UserAvatarProps {
  /** User's profile image URL (if provided, takes precedence) */
  image?: string | null;
  /** User's name (used for initials + gradient) */
  name?: string | null;
  /** User's email (fallback if no name) */
  email?: string | null;
  /** Size of the avatar (ignored if a size is supplied via className) */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Additional class names */
  className?: string;
}

const sizeClasses = {
  xs: 'h-4 w-4',
  sm: 'h-5 w-5',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
};

/**
 * Derive up to two initials.
 * - Multi-word names ("Tao Huang") → first letter of first two words ("TH").
 * - Single-word names / emails ("tao", "tao@deepguide.ai") → first letter ("T").
 */
function getInitials(name?: string | null, email?: string | null): string {
  const source = name?.trim() || email?.split('@')[0]?.trim() || '';
  if (!source) return 'U';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
}

/**
 * Deterministic gradient background derived from the seed string, so the same
 * user always gets the same colors without an external request.
 */
function gradientFor(seed: string): string {
  let hash = 0;
  const s = seed || 'user';
  for (let i = 0; i < s.length; i++) {
    hash = (hash << 5) - hash + s.charCodeAt(i);
    hash |= 0;
  }
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 55) % 360;
  return `linear-gradient(135deg, hsl(${h1} 68% 56%), hsl(${h2} 72% 46%))`;
}

/**
 * Canonical user avatar. Shows the user's photo when available, otherwise a
 * deterministic gradient with the user's initials. The initials scale with the
 * avatar via container query units, so a single component renders correctly at
 * any size (className size wins over the `size` prop).
 */
export function UserAvatar({
  image,
  name,
  email,
  size = 'sm',
  className,
}: UserAvatarProps) {
  const displayName = name || email || 'User';
  const initials = getInitials(name, email);
  // Single letters can be a touch larger than a two-letter pair.
  const fontSize = initials.length > 1 ? '34cqw' : '44cqw';

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      {image && <AvatarImage src={image} alt={displayName} />}
      <AvatarFallback
        className="font-medium text-white [container-type:inline-size]"
        style={{ background: gradientFor(displayName) }}
        delayMs={image ? 200 : 0}
      >
        <span style={{ fontSize, lineHeight: 1 }}>{initials}</span>
      </AvatarFallback>
    </Avatar>
  );
}
